<template>
    <div>
        <modal :id="modalId" v-model="modalShow" :title="title" scrollable ok-title="OK" :ok-disabled="loading">
            <busy-modal :show="loading"></busy-modal>
            <span class="photo-label-sort-wrapper">
                <draggable :sort="true" v-model="photoPlacesOrder" handle=".handle">
                    <li
                        v-for="item in photoPlacesOrder"
                        :key="item.order"
                        class="label-item handle d-flex justify-content-between align-items-center"
                    >
                        <span>{{ item.name }}</span>
                        <a
                            v-if="canDelete(item)"
                            @click.prevent="confirmDeletePlace(item)"
                            class="ml-auto btn btn-link p-0"
                            ><font-awesome-icon :icon="['fas', 'trash']"
                        /></a>
                    </li>
                </draggable>
            </span>
            <template #modal-footer>
                <b-dropdown dropup text="Add Place" variant="info" class="custom-dropup">
                    <b-form-input
                        v-model="searchQuery"
                        placeholder="Search..."
                        class="mb-2"
                        @input="filterOptions"
                    ></b-form-input>
                    <b-dropdown-item v-for="(option, index) in filteredOptions" :key="index" @click="addPlace(option)">
                        {{ option.name }}
                    </b-dropdown-item>
                </b-dropdown>
                <b-button variant="secondary" class="px-3" @click="hideModal">Cancel</b-button>
                <b-button variant="primary" class="px-3" @click="saveOrder(true)">Save</b-button>
            </template>
        </modal>

        <modal
            id="delete-place-confirmation-modal"
            title="Confirm Deletion"
            @ok="deletePlace"
            v-model="deletePlaceModalShow"
        >
            Are you sure you want to delete {{ placeToDeleteName }}? All images currently assigned to this place will be
            moved to Uncategorized.
        </modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import isNil from 'lodash/isNil';
    import isEmpty from 'lodash/isEmpty';
    import API from '../../../../api';
    import { formatResponseError } from '../../../../utils/api';
    import { RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT } from '../../../../endpoints';
    import BusyModal from '../../../generic/busy-modal/busy-modal.vue';
    import Modal from '../../../generic/modal/modal.vue';
    import { VueDraggable } from 'vue-draggable-plus';

    export default {
        name: 'OrderPlacesModal',
        components: {
            draggable: VueDraggable,
            BusyModal,
            Modal,
        },
        props: {
            modalId: String,
            update: {
                type: Function,
                default: () => Promise.resolve(),
            },
            propertyId: Number,
        },
        computed: {
            placeToDeleteName() {
                if (isNil(this.placeToDelete)) {
                    return '';
                }
                return this.placeToDelete.name;
            },
        },
        data() {
            return {
                modalShow: false,
                loading: false,
                title: 'Organize Places',
                photoPlacesOrder: [],
                photoPlacesAddOptions: [],
                searchQuery: '',
                filteredOptions: [],
                placeToDelete: null,
                deletePlaceModalShow: false,
            };
        },
        methods: {
            hideModal() {
                this.$bvModal.hide(this.modalId);
            },
            async fetchPlacesOrder() {
                try {
                    this.loading = true;
                    const response = await API.get(
                        `${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}places-data/`
                    );
                    this.photoPlacesOrder = response.data.photo_places_order;
                    this.photoPlacesAddOptions = response.data.photo_places_add_options;
                    this.filteredOptions = [...this.photoPlacesAddOptions];
                } catch (e) {
                    console.log(e);
                    this.errorToast({ text: 'Could not fetch places order.' });
                } finally {
                    this.loading = false;
                }
            },
            filterOptions() {
                if (this.searchQuery) {
                    this.filteredOptions = this.photoPlacesAddOptions.filter((option) =>
                        option.name.toLowerCase().includes(this.searchQuery.toLowerCase())
                    );
                } else {
                    this.filteredOptions = [...this.photoPlacesAddOptions];
                }
            },
            async addPlace(option) {
                try {
                    this.loading = true;
                    await this.saveOrder(false);
                    const response = await API.post(
                        `${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}place-add/${option.id}/`
                    );
                    this.fetchPlacesOrder();
                } catch (e) {
                    this.errorToast({ text: 'Could not add place.' });
                } finally {
                    this.placeToDelete = null;
                    this.loading = false;
                }
            },
            async deletePlace() {
                try {
                    this.loading = true;
                    await this.saveOrder(false);
                    const response = await API.delete(
                        `${RENTAL_PROPERTY_SETUP_GALLERY_ENDPOINT(this.propertyId)}place-delete/${
                            this.placeToDelete.id
                        }`
                    );
                    this.fetchPlacesOrder();
                } catch (e) {
                    this.errorToast({ text: 'Could not delete place.' });
                } finally {
                    this.placeToDelete = null;
                    this.loading = false;
                }
            },
            confirmDeletePlace(item) {
                if (this.loading) {
                    return;
                }
                this.placeToDelete = item;
                this.deletePlaceModalShow = true;
            },
            async saveOrder(hide) {
                this.loading = true;

                try {
                    var payload = {};
                    for (var i = 0; i < this.photoPlacesOrder.length; i++) {
                        this.photoPlacesOrder[i].order = i + 1;
                        payload[this.photoPlacesOrder[i].id] = i + 1;
                    }
                    this.$emit('success-save', payload);
                    if (hide) {
                        this.$nextTick(() => {
                            this.hideModal();
                        });
                    }
                } catch (err) {
                    const { generic, fields } = formatResponseError(err);

                    if (generic) {
                        this.errorToast({ text: generic });
                    }
                    if (!isEmpty(fields)) {
                        this.$refs.form.setErrors(fields);
                    }
                } finally {
                    this.loading = false;
                }
            },
            canDelete(item) {
                const nonDeletableTypes = ['BEDROOM', 'HALF_BATHROOM', 'FULL_BATHROOM'];
                return !nonDeletableTypes.includes(item.place_type.sysname);
            },
        },
        watch: {
            modalShow(newValue) {
                if (newValue === true) {
                    this.fetchPlacesOrder();
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    .photo-label-sort-wrapper {
        display: grid;
        place-items: center;
        list-style: none;
    }

    :deep(.label-item) {
        border-radius: 5px;
        padding: 5px 10px;
        border: 1px solid #0d47a1;
        color: #0d47a1;
        background-color: #fff;
        cursor: move; /* fallback if grab cursor is unsupported */
        cursor: grab;
        cursor: -moz-grab;
        cursor: -webkit-grab;
        width: 300px;
        margin-top: 10px;
        font-weight: bold;
        text-align: center;
        -webkit-user-select: none; /* Safari */
        -moz-user-select: none; /* Firefox */
        -ms-user-select: none; /* IE10+/Edge */
        user-select: none; /* Standard */
    }

    :deep(.dropdown-menu) {
        max-height: 200px;
        min-height: 200px;
        min-width: 180px !important;
        overflow-y: auto;
        padding: 0.5rem 0.5rem !important;
    }

    :deep(dropdown-item) {
        padding: 0.25rem 0.5rem !important;
    }
</style>
